import { useCallback, useEffect, useState } from 'react';
import { HIDE_DEPENDENCY_MODAL } from 'constant/constant';
import { useGetAlignPhaseDatesSetting } from 'queries/advanced-settings/advanced-settings';
import { tenMinutes } from 'queries/constants';
import {
  DateShiftUpdates,
  Entity,
  EntityType,
  getDateShiftsForEntity,
  List,
} from 'utils/date-shift';

export function useDependencyModalStates(
  taskList: List,
  phaseList: List,
  sprintList: List,
) {
  const [showDependencyModal, setShowDependencyModal] = useState(false);
  const [taskUpdatesState, setTaskUpdates] = useState<DateShiftUpdates>();
  const [phaseUpdatesState, setPhaseUpdates] = useState<DateShiftUpdates>();
  const [sprintUpdatesState, setSprintUpdates] = useState<DateShiftUpdates>();
  const alignPhaseDates = useGetAlignPhaseDatesSetting();
  const [entity, setEntity] = useState<Entity>({
    id: 0,
    startDate: null,
    dueDate: null,
    type: EntityType.Task,
    depth: 0,
    name: '',
  });
  const [datePickerLoader, setDatePickerLoader] = useState(false);
  const [dependencyModalTimeout, setDependencyModalTimeout] = useState(false);

  const onClosedDependencyModal = useCallback(() => {
    setShowDependencyModal(false);
    setEntity({
      id: 0,
      startDate: null,
      dueDate: null,
      type: EntityType.Task,
      depth: 0,
      name: '',
    });
    setTaskUpdates(undefined);
    setPhaseUpdates(undefined);
    setSprintUpdates(undefined);
    setDatePickerLoader(false);
  }, []);

  useEffect(() => {
    const timestamp = localStorage.getItem(HIDE_DEPENDENCY_MODAL);
    let timer;

    if (timestamp) {
      const currentTime = Date.now();
      const diff = currentTime - parseInt(timestamp, 10);

      if (diff < tenMinutes) {
        setDependencyModalTimeout(true);
        timer = setTimeout(() => {
          setDependencyModalTimeout(false);
          localStorage.removeItem(HIDE_DEPENDENCY_MODAL);
        }, tenMinutes - diff);
      } else {
        localStorage.removeItem(HIDE_DEPENDENCY_MODAL);
        setDependencyModalTimeout(false);
      }
    }

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    let timer;

    if (
      dependencyModalTimeout &&
      !localStorage.getItem(HIDE_DEPENDENCY_MODAL)
    ) {
      localStorage.setItem(HIDE_DEPENDENCY_MODAL, Date.now().toString());
      timer = setTimeout(() => {
        setDependencyModalTimeout(false);
        localStorage.removeItem(HIDE_DEPENDENCY_MODAL);
      }, tenMinutes);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [dependencyModalTimeout]);

  const checkForDependencyConflicts = useCallback(
    (id: number, payload, type: EntityType) => {
      if (
        taskList[id] == null &&
        phaseList[id] == null &&
        sprintList[id] == null
      ) {
        return false;
      }

      const _entity: Entity = {
        id,
        ...payload,
        type,
        depth:
          type === EntityType.Task
            ? taskList[id].depth
            : type === EntityType.Phase
            ? phaseList[id].depth
            : sprintList[id].depth,
        name:
          type === EntityType.Task
            ? taskList[id].name
            : type === EntityType.Phase
            ? phaseList[id].name
            : sprintList[id].name,
      };

      const [
        phaseUpdates,
        taskUpdates,
        sprintUpdates,
        doesEntityAffectOtherEntities,
      ] = getDateShiftsForEntity(phaseList, taskList, sprintList, _entity, {
        alignPhaseWithTask: alignPhaseDates,
      });

      if (doesEntityAffectOtherEntities && !dependencyModalTimeout) {
        setTaskUpdates(taskUpdates);
        setPhaseUpdates(phaseUpdates);
        setSprintUpdates(sprintUpdates);
        setEntity(_entity);
        setShowDependencyModal(true);
        setDatePickerLoader(true);
      }

      return doesEntityAffectOtherEntities && !dependencyModalTimeout;
    },
    [alignPhaseDates, dependencyModalTimeout, phaseList, sprintList, taskList],
  );

  return {
    showDependencyModal,
    setShowDependencyModal,
    taskList,
    phaseList,
    sprintList,
    taskUpdatesState,
    setTaskUpdates,
    phaseUpdatesState,
    setPhaseUpdates,
    sprintUpdatesState,
    setSprintUpdates,
    entity,
    setEntity,
    onClosedDependencyModal,
    datePickerLoader,
    setDatePickerLoader,
    setDependencyModalTimeout,
    dependencyModalTimeout,
    checkForDependencyConflicts,
  };
}
